import { FC, useEffect, useState } from "react";
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { PrimaryButton, SecondaryButton } from "../components/Buttons";
import { useAppInventory, useAppSelector } from "../store/hooks";
import HorizontalPadding from "../components/HorizontalPadding";
import { clearInventoryValues } from "../store/slices/inventory";
import { useNavigate } from "react-router-dom";
import InventoryDraftTable from "./Inventory/InventoryDraftTable";
import InventoryGeneratedTable from "./Inventory/InventoryGeneratedTable";
import Modal from "../components/Modal";
import { InventoryHeaderInterface, StatusInventory } from "../interfaces/InventoryInterface";
import { getInventoryHeaders, updateInventoryStatus } from "../services/inventoryServices";
import { alertService, loaderService } from "../services";

const Inventory: FC = () => {
  const invontory = useAppInventory();
  const navigate = useNavigate();
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);

  const [loadingInventory, setloadingInventory] = useState(true);
  const [openNewInventoryWarning, setOpenNewInventoryWarning] = useState(false);
  const [postponedInventoryList, setPostponedInventoryList] = useState<
    InventoryHeaderInterface[]
  >([]);

  const handleNewInventoryClick = () => {
    if (postponedInventoryList.length > 0) {
      setOpenNewInventoryWarning(true);
      return;
    }

    invontory(clearInventoryValues());
    navigate("/inventory/create");
  };

  const handleNewManifest = async () => {
    const userLogin = user.user?.userLogin;

    if (!userLogin) {
      alertService.error(
        "No hay un nombre de usuario asociado a la sesión actual"
      );
      return;
    }

    for (const invontory of postponedInventoryList) {
      const InventoryHeaderID = invontory.inventoryHeaderID;

      if (!InventoryHeaderID) {
        continue;
      }

      const response = await updateInventoryStatus(
        InventoryHeaderID,
        StatusInventory.DISCARDED,
        userLogin
      );
  
      if (response.didError) {
        alertService.error(
          `Hubo un error anulando el inventario ${invontory.inventoryHeaderCode}`,
          response.errorMessage
        );
        return;
      }
    }

    invontory(clearInventoryValues());
    navigate("/inventory/create");
  }

  useEffect(() => {
    const buCode = user.businessUnit?.code;

    if (!buCode) {
      return;
    }

    const afunction = async () => {
      const postponedInventoryList = await getInventoryHeaders(
        undefined,
        undefined,
        buCode,
        StatusInventory.PROCESS
      );

      if (postponedInventoryList.didError || !postponedInventoryList.model) {
        setloadingInventory(false);
        return;
      }

      setPostponedInventoryList(
        postponedInventoryList.model || []
      );
      setloadingInventory(false);
    };

    afunction();
  }, [user]);

  return (
    <main className="lg:pl-72 pb-32">
      <div className="py-6 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32">
        <header className="ml-4 text-2xl font-bold text-gray-700 ">
          Inventario
        </header>

        <PrimaryButton
          disabled={loading || loadingInventory}
          onClick={handleNewInventoryClick}
          className="pr-4"
        >
          <DocumentTextIcon
            className="h-5 w-5 flex-none text-gray-200 mr-1"
            aria-hidden="true"
          />
          Nuevo
        </PrimaryButton>
      </div>

      <HorizontalPadding paddingTop>
        <div className="flex flex-col w-full gap-12">
          <InventoryDraftTable
            loadingInventory={loadingInventory}
            postponedInventoryList={postponedInventoryList}
          />

          <InventoryGeneratedTable />
        </div>
      </HorizontalPadding>

      <Modal
        openModal={openNewInventoryWarning}
        setOpenModal={setOpenNewInventoryWarning}
      >
        <div className="flex flex-col gap-4 max-w-md mx-auto items-center">
          <div className="flex flex-col items-center">
            <ExclamationTriangleIcon className="text-orange-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Crear nuevo Inventario
            </h2>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-gray-600 text-center whitespace-pre-line">
              Ya existen inventarios, crear uno nuevo anulará los
              existentes ¿Estás seguro de que quieres continuar con el proceso?
            </p>
          </div>

          <div className="flex w-full flex-row gap-3 items-center justify-between mt-4">
            <SecondaryButton
              onClick={() => setOpenNewInventoryWarning(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              onClick={async () => {
                loaderService.start();
                await handleNewManifest();
                loaderService.stop();
                setOpenNewInventoryWarning(false);
              }}
              className="px-4 py-2"
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default Inventory;
